import React, { useContext } from 'react'
import { Stack } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { TTSVolume } from './VolumeButton'
import { Button } from 'antd'
import { CaiPopupContext } from 'src/context/CaiPopupContext'

async function generate_tts_blob(chunk, language = 'en') {
  if (!chunk) return

  const requestData = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text: chunk, language: language }),
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_CARCH_ENDPOINT}/tts`,
      requestData,
    )
    if (response.ok) {
      const audioBase64 = await response.text()
      const audioData = atob(audioBase64)
      const audioBlob = new Uint8Array(audioData.length)
      audioBlob.set(Array.from(audioData, (char) => char.charCodeAt(0)))
      return new Blob([audioBlob], { type: 'audio/mpeg' })
    } else {
      console.error('API request failed:', response.status, response.statusText)
      return null
    }
  } catch (error) {
    console.error(error)
  }
}

function TTSPlayer({ tourOpen = false, messages }) {
  const { audio, setAudio } = useContext(CaiPopupContext)
  const [newTtsSentences, setNewTtsSentences] = React.useState('')
  const [ttsBlobs, setTtsBlobs] = React.useState([])
  const [audioPlaying, setAudioPlaying] = React.useState(false)
  const [newTtsProcessing, setNewTtsProcessing] = React.useState(false)
  const [ttsEnabled, setTtsEnabled] = React.useState(!!tourOpen)
  const { i18n } = useTranslation()
  const intervalRef = React.useRef()
  const ttsLastIdx = React.useRef(0)
  const chatMessagesRef = React.useRef(messages)

  const resetAudio = () => {
    setTtsBlobs([])
    setAudioPlaying(false)
    ttsLastIdx.current = 0
    intervalRef.current = null
  }

  const playAudioBlob = (blob) => {
    if (!(blob instanceof Blob)) {
      console.log('Not a blob')
      return Promise.reject(new Error('The input is not a Blob.'))
    }
    const objectURL = URL.createObjectURL(blob)
    const tempAudio = new Audio(objectURL)
    setAudio(tempAudio)
    return new Promise((resolve, reject) => {
      tempAudio.addEventListener('ended', () => {
        URL.revokeObjectURL(objectURL)
        resolve()
        setAudioPlaying(false)
        setAudio()
      })
      tempAudio.play().catch((error) => {
        URL.revokeObjectURL(objectURL)
        reject(error)
      })
    })
  }

  React.useEffect(() => {
    const isTtsEnabled = sessionStorage.getItem('caiTtsEnabled')
    setTtsEnabled(false)
    // console.log('---> tts enabled', isTtsEnabled)
    if (isTtsEnabled === null || isTtsEnabled === 'true') {
      setTtsEnabled(true)
      sessionStorage.setItem('caiTtsEnabled', true)
    } else {
      setTtsEnabled(false)
      sessionStorage.setItem('caiTtsEnabled', false)
    }
  }, [])

  React.useEffect(() => {
    if (tourOpen) {
      setTtsEnabled(false)
    }
  }, [tourOpen])

  React.useEffect(() => {
    // do not play on first message init
    if (chatMessagesRef.current.length === 0 && messages.length > 2) {
      chatMessagesRef.current = messages
      return
    }
    chatMessagesRef.current = messages

    if (audio) {
      audio.pause()
      audio.currentTime = 0
      setNewTtsProcessing(false)
    }

    if (
      messages.length !== 0 &&
      messages[messages.length - 1].owner !== 'user' &&
      !newTtsProcessing &&
      ttsEnabled
    ) {
      resetAudio()
      setNewTtsProcessing(true)
    }
  }, [messages])

  // if tts active, process streaming text
  React.useEffect(() => {
    if (newTtsProcessing && ttsEnabled) {
      // Every 2 seconds, check and process new sentences
      intervalRef.current = setInterval(() => {
        if (chatMessagesRef.current.at(-1)?.owner === 'user') return
        const lastMessage = chatMessagesRef.current.at(-1)?.message
        // If no text changes, assume message finished
        if (lastMessage?.length === ttsLastIdx.current) {
          setNewTtsProcessing(false)
          ttsLastIdx.current = 0
          clearInterval(intervalRef.current)
          return
        }
        const targetText = lastMessage?.slice(ttsLastIdx.current)
        if (!targetText?.trim()) {
          setNewTtsProcessing(false)
          ttsLastIdx.current = 0
          clearInterval(intervalRef.current)
          return
        }
        // split by sentences
        const newSentences = targetText?.split(
          /(?<!\w\.\w.)(?<![A-Z][a-z]\.)(?<!\d\.)(?<=\.{1,3}|\?|\n|!)\s+/gm,
        )
        // if last item not sentence, remove it
        const lastSentence = newSentences?.at(-1)
        const isComplete = /(\.{1,3}|[!?])(["'”’)]?)\s*$/.test(
          lastSentence || '',
        )
        if (!isComplete) newSentences?.pop()
        if (newSentences?.length > 0) {
          // if first sentence then process, else process every second sentence
          var newTtsText
          newTtsText = newSentences[0]
          ttsLastIdx.current = ttsLastIdx.current + newTtsText?.length
          setNewTtsSentences(newTtsText)
        }
      }, 1000)
    }

    // Cleanup function to clear the interval
    return () => clearInterval(intervalRef.current)
  }, [newTtsProcessing])

  React.useEffect(() => {
    if (!ttsEnabled && audio) {
      audio.pause()
    } else if (ttsEnabled && audio) {
      audio.play()
    }
  }, [ttsEnabled, audio])

  React.useLayoutEffect(() => {
    const generateBlob = async () => {
      if (newTtsSentences === '') return
      const ttsBlob = await generate_tts_blob(newTtsSentences, i18n.language)
      setTtsBlobs((prevBlobs) => [...prevBlobs, ttsBlob])
    }
    generateBlob()
  }, [newTtsSentences])

  React.useLayoutEffect(() => {
    const playAudio = async () => {
      // play next audio chunk if ready
      if (ttsBlobs.length > 0 && !audioPlaying) {
        setAudioPlaying(true)
        const targetBlob = ttsBlobs[0]
        setTtsBlobs(ttsBlobs.slice(1))
        playAudioBlob(targetBlob)
      }
    }
    playAudio()
  }, [ttsBlobs, audioPlaying])

  // ... (move the other TTS-related useEffect hooks here) ...

  return (
    <Stack className="justify-content-end" direction="horizontal" gap={2}>
      <Button
        className=" p-0 d-flex justify-content-center align-items-center hover-disable btn btn-outline-primary"
        style={{ width: 40, height: 40 }}
        onClick={() => {
          setTtsEnabled((prevState) => {
            const newState = !prevState
            sessionStorage.setItem('caiTtsEnabled', newState)
            return newState
          })
        }}
      >
        <i
          style={{ color: 'var(--icon-color)' }}
          className={`${
            ttsEnabled ? 'icofont-volume-down' : 'icofont-volume-mute'
          } fs-4`}
        ></i>
      </Button>
      {ttsEnabled && <TTSVolume audio={audio} />}
    </Stack>
  )
}

export default TTSPlayer
